/* eslint-disable no-lonely-if */
import React, { useEffect, useState, useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import PropTypes from "prop-types";
import LaunchIcon from "@mui/icons-material/Launch";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import { ORGANIZATION_STRUCTURE_FIELDS } from "../../../contants/formFields";
import IntroductionFormApi from "../../../services/api/introductionForm";
import ZoneLocator from "./ZoneLocator/ZoneLocator";
import Styles from "./OrganizationStructure.styles";
import AutoCompleteDropdown from "../new/AutoCompleteDropdown/AutoCompleteDropdown";
import { getOptions } from "../../../utils";
import { SET_ORGANISATION_STRUCTURE } from "../../../redux/actionTypes/meta";
import { isEmptyObj } from "../../../utils/helper";
import { OS_REDUX_KEY_MAPPER } from "../../../contants";
import Colors from "../../../theme/colors";

function OrganizationStructureV2({
  values,
  errors,
  setFieldValue,
  showZoneLocator,
  showBlock,
  containerStyle,
  showTitle,
  useHiddenInput,
  storeValuesInRedux,
  shouldFetchOtherOS,
  disabledOSKeys,
  requiredOSKeys,
  hiddenFields,
  formFieldNames,
}) {
  const [areaData, setAreadData] = useState([]);
  const [clusterData, setClusterData] = useState([]);
  const [zoneData, setZoneData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [chapterData, setChapterData] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [blockData, setBlockData] = useState([]);
  const [isZoneLocatorOpen, setIsZoneLocatorOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const formRequestRef = useRef(true);
  const dispatch = useDispatch();

  const getData = async (api, setState, params, setter) => {
    try {
      let response;
      if (params) {
        response = await api({ data: params });
      } else {
        response = await api({});
      }
      setState(response?.data || response || []);
      if (setter) {
        setter();
      }
      if (storeValuesInRedux) {
        const key = isEmptyObj(params)
          ? "areas"
          : OS_REDUX_KEY_MAPPER[Object.keys(params)[0]];

        dispatch({
          type: SET_ORGANISATION_STRUCTURE,
          payload: {
            [key]: response?.data,
          },
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const openZoneLocator = () => {
    setIsZoneLocatorOpen(true);
  };

  const handleZoneLocationSelection = (selectedZoneData) => {
    setRegionData([]);
    setChapterData([]);
    setDistrictData([]);
    setBlockData([]);
    setFieldValue(formFieldNames.REGION, "");
    setFieldValue(formFieldNames.CHAPTER, "");
    setFieldValue(formFieldNames.DISTRICT, "");
    setFieldValue(formFieldNames.BLOCK, "");

    setFieldValue(formFieldNames.AREA, selectedZoneData.areaId);

    if (selectedZoneData.areaId) {
      getData(
        IntroductionFormApi.getAllZones,
        setZoneData,
        {
          areaId: selectedZoneData.areaId,
        },
        () => setFieldValue(formFieldNames.ZONE, selectedZoneData.zoneId)
      );
    }

    if (selectedZoneData.zoneId) {
      getData(IntroductionFormApi.getAllRegions, setRegionData, {
        zoneId: selectedZoneData.zoneId,
      });
    }

    setIsZoneLocatorOpen(false);
  };

  const fetchAllOtherOS = async () => {
    const promises = [];

    if (values.areaId && !hiddenFields.includes(formFieldNames.CLUSTER)) {
      promises.push(
        getData(IntroductionFormApi.getAllClusters, setClusterData, {
          areaId: values.areaId,
        })
      );
    }
    if (values.clusterId && !hiddenFields.includes(formFieldNames.ZONE)) {
      promises.push(
        getData(IntroductionFormApi.getAllZones, setZoneData, {
          clusterId: values.clusterId,
        })
      );
    }
    if (values.zoneId && !hiddenFields.includes(formFieldNames.REGION)) {
      promises.push(
        getData(IntroductionFormApi.getAllRegions, setRegionData, {
          zoneId: values.zoneId,
        })
      );
    }
    if (values.regionId && !hiddenFields.includes(formFieldNames.CHAPTER)) {
      promises.push(
        getData(IntroductionFormApi.getAllChapters, setChapterData, {
          regionId: values.regionId,
        })
      );
    }
    if (values.chapterId) {
      promises.push(
        getData(IntroductionFormApi.getAllDistricts, setDistrictData, {
          chapterId: values.chapterId,
        })
      );
    }
    if (values.districtId) {
      promises.push(
        getData(IntroductionFormApi.getAllBlocks, setBlockData, {
          districtId: values.districtId,
        })
      );
    }

    await Promise.all(promises);
    setIsInitialLoad(false);
  };

  const getHideStyle = (field) =>
    hiddenFields.includes(field) ? { display: "none" } : {};

  useEffect(() => {
    const initializeData = async () => {
      await getData(IntroductionFormApi.getAllArea, setAreadData);

      // Check if form already has values (from parent component/Formik)
      const hasInitialValues =
        values && Object.values(values).some((val) => val);

      // Only use sessionStorage if form is empty initially (means it's a refresh)
      if (!hasInitialValues) {
        const savedValues = sessionStorage.getItem(
          "organizationStructureValues"
        );
        let restoredValues = {};

        if (savedValues) {
          restoredValues = JSON.parse(savedValues);
          Object.keys(restoredValues).forEach((key) => {
            if (restoredValues[key]) {
              setFieldValue(key, restoredValues[key]);
            }
          });

          // Fetch dependent dropdowns using restored values
          if (
            restoredValues.areaId &&
            !hiddenFields.includes(formFieldNames.CLUSTER)
          ) {
            await getData(IntroductionFormApi.getAllClusters, setClusterData, {
              areaId: restoredValues.areaId,
            });
          }
          if (
            restoredValues.clusterId &&
            !hiddenFields.includes(formFieldNames.ZONE)
          ) {
            await getData(IntroductionFormApi.getAllZones, setZoneData, {
              clusterId: restoredValues.clusterId,
            });
          }
          if (
            restoredValues.zoneId &&
            !hiddenFields.includes(formFieldNames.REGION)
          ) {
            await getData(IntroductionFormApi.getAllRegions, setRegionData, {
              zoneId: restoredValues.zoneId,
            });
          }
          if (
            restoredValues.regionId &&
            !hiddenFields.includes(formFieldNames.CHAPTER)
          ) {
            await getData(IntroductionFormApi.getAllChapters, setChapterData, {
              regionId: restoredValues.regionId,
            });
          }
          if (restoredValues.chapterId) {
            await getData(
              IntroductionFormApi.getAllDistricts,
              setDistrictData,
              {
                chapterId: restoredValues.chapterId,
              }
            );
          }
          if (restoredValues.districtId) {
            await getData(IntroductionFormApi.getAllBlocks, setBlockData, {
              districtId: restoredValues.districtId,
            });
          }
        }
      } else {
        // Form has initial values, fetch dependent dropdowns normally
        if (shouldFetchOtherOS || values.areaId) {
          await fetchAllOtherOS();
        }
      }

      setIsInitialLoad(false);
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (!isInitialLoad && shouldFetchOtherOS && formRequestRef.current) {
      fetchAllOtherOS();
      formRequestRef.current = false;
    }
  }, [values, shouldFetchOtherOS, isInitialLoad]);

  // Save values to sessionStorage whenever they change (but only if values exist)
  useEffect(() => {
    const hasAnyValue = values && Object.values(values).some((val) => val);

    if (hasAnyValue) {
      sessionStorage.setItem(
        "organizationStructureValues",
        JSON.stringify(values)
      );
    }
  }, [values]);

  // Clear sessionStorage when component unmounts (logout/navigation)
  useEffect(() => {
    return () => {
      sessionStorage.removeItem("organizationStructureValues");
    };
  }, []);

  return (
    <>
      {showTitle && (
        <Typography style={Styles.typography}>
          Organization Structure
        </Typography>
      )}
      <Grid
        container
        style={{
          border: "1px solid #C4C4C4",
          padding: 20,
          ...(containerStyle ?? {}),
        }}
      >
        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            key={`area-${areaData.length}`}
            getOptionLabel={(option) => option.name}
            label="Area"
            name={formFieldNames.AREA}
            options={getOptions(areaData, "structureId")}
            error={errors[formFieldNames.AREA]}
            value={values[formFieldNames.AREA] || ""}
            inputProps={{
              required: requiredOSKeys.includes(formFieldNames.AREA),
            }}
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(formFieldNames.AREA, "");
              } else {
                setFieldValue(formFieldNames.AREA, selectedValue.value);
                getData(IntroductionFormApi.getAllClusters, setClusterData, {
                  areaId: selectedValue.value,
                });
              }
              setFieldValue(formFieldNames.CLUSTER, "");
              setFieldValue(formFieldNames.ZONE, "");
              setFieldValue(formFieldNames.REGION, "");
              setFieldValue(formFieldNames.CHAPTER, "");
              setFieldValue(formFieldNames.DISTRICT, "");
              setClusterData([]);
              setZoneData([]);
              setRegionData([]);
              setChapterData([]);
              setDistrictData([]);
              if (showBlock) {
                setFieldValue(formFieldNames.BLOCK, "");
                setBlockData([]);
              }
            }}
            useHiddenInput={useHiddenInput}
            disabled={disabledOSKeys.includes(formFieldNames.AREA)}
          />
        </Grid>
        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            key={`cluster-${clusterData.length}`}
            getOptionLabel={(option) => option.name}
            label="Cluster"
            name={formFieldNames.CLUSTER}
            options={getOptions(clusterData, "structureId")}
            error={errors[formFieldNames.CLUSTER]}
            value={values[formFieldNames.CLUSTER] || ""}
            disabled={
              !values[formFieldNames.AREA] ||
              disabledOSKeys.includes(formFieldNames.CLUSTER)
            }
            inputProps={{
              required: requiredOSKeys.includes(formFieldNames.CLUSTER),
            }}
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(formFieldNames.CLUSTER, "");
              } else {
                setFieldValue(formFieldNames.CLUSTER, selectedValue.value);
                getData(IntroductionFormApi.getAllZones, setZoneData, {
                  clusterId: selectedValue.value,
                });
              }
              setFieldValue(formFieldNames.ZONE, "");
              setFieldValue(formFieldNames.REGION, "");
              setFieldValue(formFieldNames.CHAPTER, "");
              setFieldValue(formFieldNames.DISTRICT, "");
              setZoneData([]);
              setRegionData([]);
              setChapterData([]);
              setDistrictData([]);
              if (showBlock) {
                setFieldValue(formFieldNames.BLOCK, "");
                setBlockData([]);
              }
            }}
            useHiddenInput={useHiddenInput}
          />
        </Grid>
        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            key={`zone-${zoneData.length}`}
            getOptionLabel={(option) => option.name}
            label="Zone"
            name={formFieldNames.ZONE}
            options={getOptions(zoneData, "structureId")}
            error={errors[formFieldNames.ZONE]}
            value={values[formFieldNames.ZONE] || ""}
            disabled={
              !values[formFieldNames.CLUSTER] ||
              disabledOSKeys.includes(formFieldNames.ZONE)
            }
            inputProps={{
              required: requiredOSKeys.includes(formFieldNames.ZONE),
            }}
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(formFieldNames.ZONE, "");
              } else {
                setFieldValue(formFieldNames.ZONE, selectedValue.value);
                getData(IntroductionFormApi.getAllRegions, setRegionData, {
                  zoneId: selectedValue.value,
                });
              }
              setFieldValue(formFieldNames.REGION, "");
              setFieldValue(formFieldNames.CHAPTER, "");
              setFieldValue(formFieldNames.DISTRICT, "");
              setRegionData([]);
              setChapterData([]);
              setDistrictData([]);
              if (showBlock) {
                setFieldValue(formFieldNames.BLOCK, "");
                setBlockData([]);
              }
            }}
            useHiddenInput={useHiddenInput}
          />
        </Grid>
        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            key={`region-${regionData.length}`}
            getOptionLabel={(option) => option.name}
            label="Region"
            name={formFieldNames.REGION}
            options={getOptions(regionData, "structureId")}
            error={errors[formFieldNames.REGION]}
            disabled={
              !values[formFieldNames.ZONE] ||
              disabledOSKeys.includes(formFieldNames.REGION)
            }
            inputProps={{
              required: requiredOSKeys.includes(formFieldNames.REGION),
            }}
            value={values[formFieldNames.REGION] || ""}
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(formFieldNames.REGION, "");
              } else {
                setFieldValue(formFieldNames.REGION, selectedValue.value);
                getData(IntroductionFormApi.getAllChapters, setChapterData, {
                  regionId: selectedValue.value,
                });
              }
              setFieldValue(formFieldNames.CHAPTER, "");
              setFieldValue(formFieldNames.DISTRICT, "");
              setChapterData([]);
              setDistrictData([]);
              if (showBlock) {
                setFieldValue(formFieldNames.BLOCK, "");
                setBlockData([]);
              }
            }}
            useHiddenInput={useHiddenInput}
          />
        </Grid>
        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            key={`chapter-${chapterData.length}`}
            getOptionLabel={(option) => option.name}
            label="Chapter"
            name={formFieldNames.CHAPTER}
            options={getOptions(chapterData, "structureId")}
            error={errors[formFieldNames.CHAPTER]}
            value={values[formFieldNames.CHAPTER] || ""}
            disabled={
              !values[formFieldNames.REGION] ||
              disabledOSKeys.includes(formFieldNames.CHAPTER)
            }
            inputProps={{
              required: requiredOSKeys.includes(formFieldNames.CHAPTER),
            }}
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(formFieldNames.CHAPTER, "");
              } else {
                setFieldValue(formFieldNames.CHAPTER, selectedValue.value);
                getData(IntroductionFormApi.getAllDistricts, setDistrictData, {
                  chapterId: selectedValue.value,
                });
              }
              setFieldValue(formFieldNames.DISTRICT, "");
              setDistrictData([]);
              setBlockData([]);
            }}
            style={{ ...getHideStyle(formFieldNames.CHAPTER) }}
            useHiddenInput={useHiddenInput}
          />
        </Grid>
        <Grid item md={12} xs={12} style={Styles.gridStyle}>
          <AutoCompleteDropdown
            key={`district-${districtData.length}`}
            getOptionLabel={(option) => option.name}
            label="District"
            name={formFieldNames.DISTRICT}
            options={getOptions(districtData, "structureId")}
            error={errors[formFieldNames.DISTRICT]}
            value={values[formFieldNames.DISTRICT] || ""}
            disabled={
              !values[formFieldNames.CHAPTER] ||
              disabledOSKeys.includes(formFieldNames.DISTRICT)
            }
            inputProps={{
              required: requiredOSKeys.includes(formFieldNames.DISTRICT),
            }}
            onChange={(selectedValue) => {
              if (!selectedValue) {
                setFieldValue(formFieldNames.DISTRICT, "");
              } else {
                setFieldValue(formFieldNames.DISTRICT, selectedValue.value);
                if (showBlock) {
                  getData(IntroductionFormApi.getAllBlocks, setBlockData, {
                    districtId: selectedValue.value,
                  });
                }
              }
              setFieldValue(formFieldNames.BLOCK, "");
              setBlockData([]);
            }}
            useHiddenInput={useHiddenInput}
            style={{ ...getHideStyle(formFieldNames.DISTRICT) }}
          />
        </Grid>
        {showBlock ? (
          <Grid item md={12} xs={12} style={Styles.gridStyle}>
            <AutoCompleteDropdown
              key={`block-${blockData.length}`}
              getOptionLabel={(option) => option.name}
              label="Block"
              name={formFieldNames.BLOCK}
              options={getOptions(blockData, "structureId")}
              error={errors[formFieldNames.BLOCK]}
              value={values[formFieldNames.BLOCK] || ""}
              disabled={
                !values[formFieldNames.DISTRICT] ||
                disabledOSKeys.includes(formFieldNames.BLOCK)
              }
              inputProps={{
                required: requiredOSKeys.includes(formFieldNames.BLOCK),
              }}
              onChange={(selectedValue) => {
                if (!selectedValue) {
                  setFieldValue(formFieldNames.BLOCK, "");
                } else {
                  setFieldValue(formFieldNames.BLOCK, selectedValue.value);
                }
              }}
              useHiddenInput={useHiddenInput}
            />
          </Grid>
        ) : null}
      </Grid>
      {showZoneLocator && (
        <Box
          sx={{
            borderBottom: "2px solid #003E99",
            width: "46%",
            "&:hover": { borderBottom: "2px solid #0054CC" },
          }}
          onClick={openZoneLocator}
        >
          <Typography
            sx={{
              color: Colors.midnightBlue,
              "&:hover": {
                color: Colors.hoverBlue,
              },
              ...Styles.zoneLocatorContainer,
              alignItems: "center",
              fontSize: "18px",
              fontWeight: 600,
            }}
          >
            Zone Locator
            <LaunchIcon
              sx={{
                color: Colors.midnightBlue,
                "&:hover": {
                  color: Colors.hoverBlue,
                },
                marginLeft: 5,
                fontSize: 20,
              }}
            />
          </Typography>
        </Box>
      )}
      {isZoneLocatorOpen ? (
        <ZoneLocator
          handleClose={() => {
            setIsZoneLocatorOpen(false);
          }}
          handleSelectedOption={handleZoneLocationSelection}
        />
      ) : null}
    </>
  );
}

const propTypes = {
  errors: PropTypes.objectOf(PropTypes.string).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  showZoneLocator: PropTypes.bool,
  showBlock: PropTypes.bool,
  containerStyle: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  showTitle: PropTypes.bool,
  requiredOSKeys: PropTypes.arrayOf(PropTypes.string),
  useHiddenInput: PropTypes.bool,
  storeValuesInRedux: PropTypes.bool,
  shouldFetchOtherOS: PropTypes.bool,
  disabledOSKeys: PropTypes.arrayOf(PropTypes.string),
  hiddenFields: PropTypes.arrayOf(PropTypes.string),
  formFieldNames: PropTypes.objectOf(PropTypes.string),
};

const defaultProps = {
  showZoneLocator: true,
  showBlock: false,
  containerStyle: null,
  showTitle: true,
  requiredOSKeys: [],
  useHiddenInput: false,
  storeValuesInRedux: false,
  shouldFetchOtherOS: false,
  disabledOSKeys: [],
  hiddenFields: [],
  formFieldNames: ORGANIZATION_STRUCTURE_FIELDS,
};

OrganizationStructureV2.propTypes = propTypes;
OrganizationStructureV2.defaultProps = defaultProps;

export default OrganizationStructureV2;
